/**
 * AGENT-BC2: "Qty Quoted vs Qty Sold" line chart.
 *
 * Mirrors the PBI HSQ-page bookmark navigator `cdd356ff0a6b70ea394c` theme.
 * No existing PBI combo visual maps cleanly to this pairing (every candidate
 * bundles Revenue as a bar series). The backend synthesises this payload
 * from `IA_QtyQuoted_Dynamic` + `IA_QtySold_Dynamic` aggregated by year.
 *
 * Both series are counts → single y-axis using `formatCount`. Uses the
 * blue-led `LINE_PALETTE_BLUE` so this card visually differentiates from
 * the orange-led QuotesOrdersChart sitting next to it.
 *
 * Reads `reportData.unitsQuotedSoldChart` from the shared `useReportData` hook.
 */

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { dashboardChartTheme } from "../tokens";
import {
  AXIS_LABEL_FONT_SIZE,
  buildLineSeries,
  cssVar,
  decorateLineSeries,
  formatCount,
  hexToRgba,
  LINE_PALETTE_BLUE,
  makeCountTooltipFormatter,
  makeTooltipBlock,
  SINGLE_AXIS_GRID,
  STANDARD_LEGEND,
} from "./_chartUtils";
import {
  sanitizeXAxis,
  useReportData,
  type ComboChartPayload,
  type ComboSeries,
} from "./useReportData";
import { useUiStore } from "../state/uiStore";

const CHART_ID = "unitsQuotedSold";
const FALLBACK_TITLE = "Qty Quoted vs Qty Sold";

/** Spec-mandated series colors for the Qty Quoted vs Qty Sold chart.
 *  Override the design-token fallback so this card matches the wireframe's
 *  dark-blue + light-blue dual-area look exactly. */
const QTY_QUOTED_COLOR = "#1565c0";
const QTY_SOLD_COLOR = "#42a5f5";
const AREA_OPACITY = 0.2;
const SERIES_COLORS = [QTY_QUOTED_COLOR, QTY_SOLD_COLOR] as const;

export function UnitsQuotedSoldChart(): JSX.Element {
  const { data, isLoading, isError } = useReportData();
  const theme = useUiStore((s) => s.theme);

  const payload: ComboChartPayload | undefined = data?.unitsQuotedSoldChart;
  const xAxisData = sanitizeXAxis(payload?.xAxis, CHART_ID);
  const series: ComboSeries[] = payload?.series ?? [];
  const title = (payload?.title && payload.title.trim()) || FALLBACK_TITLE;

  const hasData = xAxisData.length > 0 && series.length > 0;

  const option = useMemo(() => {
    if (!hasData) return null;

    const decorated = decorateLineSeries(series, LINE_PALETTE_BLUE);
    const builtSeries = buildLineSeries(series, decorated);
    // Override colors + area opacity per spec: dark blue (Qty Quoted) +
    // light blue (Qty Sold), each with a 20% area fill below the line.
    const mappedSeries = builtSeries.map((s: any, i: number) => {
      const color = SERIES_COLORS[i % SERIES_COLORS.length];
      return {
        ...s,
        lineStyle: { ...(s.lineStyle ?? {}), color },
        itemStyle: { ...(s.itemStyle ?? {}), color },
        areaStyle: { color: hexToRgba(color, AREA_OPACITY) },
      };
    });

    return dashboardChartTheme({
      legend: STANDARD_LEGEND,
      grid: SINGLE_AXIS_GRID,
      tooltip: makeTooltipBlock(makeCountTooltipFormatter()),
      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: false,
        axisLabel: { fontSize: AXIS_LABEL_FONT_SIZE },
      },
      yAxis: [
        {
          type: "value",
          name: "Units",
          nameLocation: "middle" as const,
          nameGap: 40,
          nameTextStyle: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            color: cssVar("--dash-chart-axis-label", "#5a6473"),
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: AXIS_LABEL_FONT_SIZE,
            formatter: (v: number) => formatCount(v),
          },
          splitLine: { show: true, lineStyle: { type: "dotted" as const } },
        },
      ],
      series: mappedSeries,
    });
  }, [hasData, series, xAxisData, theme]);

  return (
    <div className="dash-card">
      <h3 className="charts-row__title">{title}</h3>
      {isLoading ? (
        <div className="charts-row__empty charts-row__empty--shimmer" aria-busy="true">
          <span className="charts-row__shimmer" />
        </div>
      ) : isError ? (
        <div className="charts-row__empty charts-row__empty--message">Failed to load chart data</div>
      ) : !hasData || !option ? (
        <div className="charts-row__empty charts-row__empty--message">No chart data available</div>
      ) : (
        <ReactECharts
          className="charts-row__chart"
          option={option}
          notMerge
          lazyUpdate
          style={{ height: "100%", width: "100%" }}
        />
      )}
    </div>
  );
}

export default UnitsQuotedSoldChart;
